// =====================================================
// SUPER ADMIN - GESTÃO DE TIPOS DE CONTA
// =====================================================

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { accountTypesService } from '../services/account-types-service';
import { 
  AccountTypeStats, 
  PersonalAccountInfo, 
  BusinessAccountInfo, 
  AccountConversionRequest 
} from '../types/user';
import { 
  Users, 
  Building2, 
  User, 
  TrendingUp, 
  ArrowUpDown, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Crown,
  Briefcase
} from 'lucide-react';

export function SuperAdminAccountTypes() {
  const [stats, setStats] = useState<AccountTypeStats | null>(null);
  const [personalAccounts, setPersonalAccounts] = useState<PersonalAccountInfo[]>([]);
  const [businessAccounts, setBusinessAccounts] = useState<BusinessAccountInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; type: 'personal' | 'business'; name: string } | null>(null);
  const [conversionForm, setConversionForm] = useState<AccountConversionRequest>({
    tenantId: '',
    targetType: 'business'
  });
  const [converting, setConverting] = useState(false);

  // =====================================================
  // CARREGAMENTO DE DADOS
  // =====================================================

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [statsData, personalData, businessData] = await Promise.all([
        accountTypesService.getAccountStats(),
        accountTypesService.getPersonalAccounts(),
        accountTypesService.getBusinessAccounts()
      ]);

      setStats(statsData);
      setPersonalAccounts(personalData);
      setBusinessAccounts(businessData);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CONVERSÃO DE CONTAS
  // =====================================================

  const handleConversionClick = (accountId: string, currentType: 'personal' | 'business', accountName: string) => {
    setSelectedAccount({ id: accountId, type: currentType, name: accountName });
    setConversionForm({
      tenantId: accountId,
      targetType: currentType === 'personal' ? 'business' : 'personal'
    });
    setConversionModalOpen(true);
  };

  const handleConversion = async () => {
    if (!selectedAccount) return;

    setConverting(true);
    try {
      // Verificar se pode converter
      const canConvert = await accountTypesService.canConvertAccount(
        conversionForm.tenantId, 
        conversionForm.targetType
      );

      if (!canConvert.canConvert) {
        alert(`❌ Não é possível converter: ${canConvert.reason}`);
        return;
      }

      // Executar conversão
      if (conversionForm.targetType === 'personal') {
        await accountTypesService.convertToPersonal(conversionForm);
      } else {
        await accountTypesService.convertToBusiness(conversionForm);
      }

      alert(`✅ Conta convertida para ${conversionForm.targetType === 'personal' ? 'Pessoal' : 'Empresarial'} com sucesso!`);
      
      // Recarregar dados
      await loadAllData();
      setConversionModalOpen(false);
    } catch (error) {
      console.error('❌ Erro na conversão:', error);
      alert(`❌ Erro na conversão: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setConverting(false);
    }
  };

  // =====================================================
  // COMPONENTES DE ESTATÍSTICAS
  // =====================================================

  const StatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Contas Pessoais */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Contas Pessoais</CardTitle>
          <User className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats?.personal.total || 0}</div>
          <p className="text-xs text-muted-foreground">
            {stats?.personal.active || 0} ativas, {stats?.personal.premium || 0} premium
          </p>
        </CardContent>
      </Card>

      {/* Contas Empresariais */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Contas Empresariais</CardTitle>
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats?.business.total || 0}</div>
          <p className="text-xs text-muted-foreground">
            {stats?.business.active || 0} ativas, {stats?.business.premium || 0} premium
          </p>
        </CardContent>
      </Card>

      {/* Total de Usuários */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Média Usuários/Empresa</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats?.business.avgUsersPerAccount || 0}</div>
          <p className="text-xs text-muted-foreground">usuários por conta business</p>
        </CardContent>
      </Card>

      {/* Total Geral */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total de Contas</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {(stats?.personal.total || 0) + (stats?.business.total || 0)}
          </div>
          <p className="text-xs text-muted-foreground">
            {(stats?.personal.active || 0) + (stats?.business.active || 0)} ativas
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // =====================================================
  // TABELA DE CONTAS PESSOAIS
  // =====================================================

  const PersonalAccountsTable = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Contas Pessoais ({personalAccounts.length})
        </CardTitle>
        <CardDescription>
          Usuários individuais com conta própria
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Usuário</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Plano</th>
                <th className="text-left p-2">Criado em</th>
                <th className="text-left p-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {personalAccounts.map((account) => (
                <tr key={account.tenantId} className="border-b hover:bg-gray-50">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      <div>
                        <div className="font-medium">{account.userName}</div>
                        <div className="text-xs text-gray-500">ID: {account.tenantId.slice(0, 8)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2">{account.userEmail}</td>
                  <td className="p-2">
                    <Badge variant={account.plan === 'Premium' ? 'default' : 'secondary'}>
                      {account.plan}
                    </Badge>
                  </td>
                  <td className="p-2">{account.createdAt.toLocaleDateString('pt-BR')}</td>
                  <td className="p-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleConversionClick(account.tenantId, 'personal', account.userName)}
                    >
                      <ArrowUpDown className="h-4 w-4 mr-1" />
                      Converter para Empresarial
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {personalAccounts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma conta pessoal encontrada
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // =====================================================
  // TABELA DE CONTAS EMPRESARIAIS
  // =====================================================

  const BusinessAccountsTable = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Contas Empresariais ({businessAccounts.length})
        </CardTitle>
        <CardDescription>
          Empresas com múltiplos usuários
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Empresa</th>
                <th className="text-left p-2">Usuários</th>
                <th className="text-left p-2">Managers</th>
                <th className="text-left p-2">Plano</th>
                <th className="text-left p-2">Criado em</th>
                <th className="text-left p-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {businessAccounts.map((account) => (
                <tr key={account.tenantId} className="border-b hover:bg-gray-50">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-blue-500" />
                      <div>
                        <div className="font-medium">{account.companyName}</div>
                        <div className="text-xs text-gray-500">ID: {account.tenantId.slice(0, 8)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {account.userCount}
                    </div>
                  </td>
                  <td className="p-2">{account.managerCount}</td>
                  <td className="p-2">
                    <Badge variant={account.plan === 'Premium' ? 'default' : 'secondary'}>
                      {account.plan}
                    </Badge>
                  </td>
                  <td className="p-2">{account.createdAt.toLocaleDateString('pt-BR')}</td>
                  <td className="p-2">
                    {account.userCount === 1 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConversionClick(account.tenantId, 'business', account.companyName)}
                      >
                        <ArrowUpDown className="h-4 w-4 mr-1" />
                        Converter para Pessoal
                      </Button>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Não conversível ({account.userCount} usuários)
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {businessAccounts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma conta empresarial encontrada
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // =====================================================
  // MODAL DE CONVERSÃO
  // =====================================================

  const ConversionModal = () => (
    <Dialog open={conversionModalOpen} onOpenChange={setConversionModalOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Converter Tipo de Conta
          </DialogTitle>
          <DialogDescription>
            Converter "{selectedAccount?.name}" de {selectedAccount?.type === 'personal' ? 'Pessoal' : 'Empresarial'} 
            para {conversionForm.targetType === 'personal' ? 'Pessoal' : 'Empresarial'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <p>
                {conversionForm.targetType === 'personal' 
                  ? "⚠️ A conversão para conta pessoal só é possível se houver apenas 1 usuário na empresa."
                  : "⚠️ A conversão para conta empresarial permitirá múltiplos usuários e mudará as permissões."
                }
              </p>
            </div>
          </Alert>

          {conversionForm.targetType === 'business' && (
            <div className="space-y-2">
              <Label htmlFor="companyName">Nome da Empresa</Label>
              <Input
                id="companyName"
                placeholder="Digite o nome da empresa"
                value={conversionForm.companyName || ''}
                onChange={(e) => setConversionForm(prev => ({ ...prev, companyName: e.target.value }))}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setConversionModalOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConversion} 
            disabled={converting || (conversionForm.targetType === 'business' && !conversionForm.companyName)}
          >
            {converting ? 'Convertendo...' : 'Confirmar Conversão'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Carregando tipos de conta...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Gestão de Tipos de Conta</h1>
        <p className="text-gray-600">Gerencie contas pessoais e empresariais</p>
      </div>

      {/* Estatísticas */}
      <StatsCards />

      {/* Tabs de Contas */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="personal">Contas Pessoais</TabsTrigger>
          <TabsTrigger value="business">Contas Empresariais</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribuição de Tipos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4 text-yellow-500" />
                      Pessoais
                    </span>
                    <Badge>{stats?.personal.total || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      Empresariais
                    </span>
                    <Badge>{stats?.business.total || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Resumo de Atividade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Total de Contas Ativas</span>
                    <Badge variant="outline">
                      {(stats?.personal.active || 0) + (stats?.business.active || 0)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Total de Contas Premium</span>
                    <Badge variant="default">
                      {(stats?.personal.premium || 0) + (stats?.business.premium || 0)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="personal">
          <PersonalAccountsTable />
        </TabsContent>

        <TabsContent value="business">
          <BusinessAccountsTable />
        </TabsContent>
      </Tabs>

      {/* Modal de Conversão */}
      <ConversionModal />
    </div>
  );
}
